import { create } from "zustand";
import { BatchRawFileItemState } from "@presentation/common";
import { BatchRawFile, FileMetadata } from "@domain/schemas/BI";
import { State } from "@progress/kendo-data-query";
import { loadRuntimeConfig, runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

// seems like the one loaded in index.tsx is not available here, so we load it again
// eslint-disable-next-line unicorn/no-top-level-side-effects -- must run before the store below reads runtimeConfig
await loadRuntimeConfig();

export type SelectedState = {
  [id: string]: FileMetadata & { isFolder: boolean };
};

/**
Label of the root breadcrumb crumb, shared with the page so both build it the same way.
*/
export const buildRootBreadcrumbLabel = (locationName: string) => `Location: ${locationName}`;

interface FileExplorerStore {
  rawFiles: BatchRawFileItemState[];
  metaState: State;
  selectedState: SelectedState;
  breadcrumbItems: { id: string; text: string; disabled: boolean }[];
  resetSelectedGridData: () => void;
  setRawFiles: (newRawFileList: BatchRawFile[]) => void;
  setSelectedState: (newSelectedState: SelectedState) => void;
  setBreadcrumbItems: (items: { id: string; text: string; disabled: boolean }[]) => void;
  setMetaState: (state: State) => void;
}

export const useFileExplorerStore = create<FileExplorerStore>((set) => ({
  rawFiles: [],
  metaState: {
    skip: 0, // start at first page
    take: 50, // default page size
    sort: [{ field: "fileName", dir: "asc" }],
    filter: undefined, // optional, ensures no filter applied initially
  },
  selectedState: {},
  breadcrumbItems: [{ id: "home", text: buildRootBreadcrumbLabel(runtimeConfig.RAWFILE_CONTAINER_NAME), disabled: false }],
  setBreadcrumbItems: (items) => {
    set({ breadcrumbItems: items });
  },
  setRawFiles: (newRawFileList: BatchRawFile[]) => {
    set((state) => {
      const existingRawFileMap = new Map(state.rawFiles.map((rawFile) => [rawFile.id, rawFile]));
      const updatedRawFileList = newRawFileList.map((rawFile) => {
        const existingRawFile = existingRawFileMap.get(rawFile.id);

        return {
          ...rawFile,
          isSelected: existingRawFile ? existingRawFile.isSelected : false,
          isExpanded: existingRawFile ? existingRawFile.isExpanded : false,
          sizeInBytes: rawFile.sizeInBytes || 0,
        } as BatchRawFileItemState;
      });

      return { rawFiles: updatedRawFileList };
    });
  },
  setSelectedState: (newSelectedState: SelectedState) => set({ selectedState: newSelectedState }),
  resetSelectedGridData: () => {
    set({ selectedState: {} });
  },
  setMetaState: (state: State) => {
    set({ metaState: state });
  },
}));
